import React, { useEffect } from "react";
import { useForm, useFieldArray, FieldError } from "react-hook-form";
import { RootState } from "app/lib/store/store";
import { useDispatch, useSelector } from "react-redux";
import { useIncomeDetails } from "app/hooks/incomeDetails/useIncomeDetails";
import { addStepsData } from "app/lib/store/slices/steps/incomeDetails";
import { useNavigate } from "react-router-dom";
import { INCOME } from "app/constants/apiEndPoints";
import toast from "react-hot-toast";
import InputField from "app/components/common/inputs/InputField";
import RemoveItem from "app/components/common/buttons/RemoveItem";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import IncomeInputs from "app/components/common/inputs/IncomeInputs";
import FormGroup from "app/components/common/inputs/FormGroup";
import SelectField from "app/components/common/inputs/SelectField";
import AddMore from "app/components/common/buttons/AddMore";
import {
  frequencyOptions,
  otherIncomeOptions,
  propertyTypeOptions,
  holdersOptions,
} from "app/utils/staticValues";
import AutocompleteLocations from "app/components/common/AutocompleteLocations";
import { normalizeIncome } from "app/utils/normalizeIncome";
import { useDeleteItem } from "app/hooks/useDeleteItem";
import { handleApiError } from "app/utils/handleApiError";

interface Income {
  dbId?: string;
  type: string;
  amount: string;
  frequency: string;
  propertyType?: string;
  noOfTitleHolders?: string;
  propertyExpense?: string;
  propertyAddress?: string;
}
interface FormData {
  income: Income[];
}

const IncomeDetailsPartOne: React.FC = () => {
  const { mutate: deleteItem, isPending: deleteIsPending } = useDeleteItem();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutate: submitIncome, isPending } = useIncomeDetails();
  const { income } = useSelector(
    (state: RootState) => state.incomeDetails.stepsData
  );
  const applicantData = useSelector(
    (state: RootState) => state.loanTypesSteps?.stepsData?.applicants
  );
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    register,
    setValue,
    setError,
    clearErrors,
  } = useForm<FormData>({
    defaultValues: {
      income: [
        { type: "gross", amount: "", frequency: "annually" },
        {
          type: "overtime_shift_allowance",
          amount: "0",
          frequency: "annually",
        },
        { type: "bonus_commission", amount: "0", frequency: "annually" },
        { type: "gross_secondary", amount: "0", frequency: "annually" },
      ],
    },
  });
  const incomeTypes = watch("income");

  const onSubmit = (data: any) => {
    const { income } = data || {};

    const formattedIncome = income.map((item: any) => ({
      ...(item.dbId && { id: item.dbId }),
      ...(item.type === "rental_income" &&
        item.noOfTitleHolders && {
          noOfTitleHolders: Number(item.noOfTitleHolders),
        }),
      ...(item.type === "rental_income" &&
        item.propertyExpense && {
          propertyExpense: Number(item.propertyExpense),
        }),
      ...(item.type === "rental_income" &&
        item.propertyType && {
          propertyType: item.propertyType,
        }),
      ...(item.type === "rental_income" &&
        item.propertyAddress && {
          propertyAddress: item.propertyAddress,
        }),
      type: item.type,
      amount: Number(item.amount),
      frequency: item.frequency,
    }));

    submitIncome(
      {
        url: `${INCOME}/${savedIds?.applicantID}`,
        data: { income: formattedIncome },
      },
      {
        onSuccess: (data: any) => {
          const responseData: Income[] =
            data?.savedIncomes.map(
              (value: any): Income => ({
                dbId: value.id,
                type: value.type,
                amount: value.amount,
                frequency: value.frequency,
                propertyType: value.propertyType,
                propertyAddress: value.propertyAddress,
                propertyExpense: value.propertyExpense,
                noOfTitleHolders: String(value.noOfTitleHolders),
              })
            ) || [];

          if (savedIds?.applicantID) {
            navigate(`/assets-liabilities`);
            dispatch(addStepsData({ income: responseData }));
          } else {
            toast.error("Applicant ID is required to more next step!");
          }
        },
        onError: (error: any) => {
          handleApiError(error);
        },
      }
    );
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "income",
  });

  useEffect(() => {
    if (income && income?.length) {
      const parsedIncome = income.map((item: any) => ({
        ...item,
        dbId: item.dbId,
        amount: Number(item.amount),
      }));

      reset({ income: normalizeIncome(parsedIncome || []) });
    } else {
      const grossFromApplicant = income?.find(
        (item: any) => item.type === "gross"
      );
      if (grossFromApplicant) {
        reset({
          income: [
            {
              type: "gross",
              dbId: grossFromApplicant?.dbId,
              amount: String(grossFromApplicant.amount),
              frequency: grossFromApplicant.frequency || "annually",
            },
            {
              type: "overtime_shift_allowance",
              amount: "0",
              frequency: "annually",
            },
            { type: "bonus_commission", amount: "0", frequency: "annually" },
            { type: "gross_secondary", amount: "0", frequency: "annually" },
          ],
        });
      }
    }
  }, [income, reset]);

  const handlePrevious = () => {
    if (savedIds.employmentID) {
      navigate(`/employment-details`);
    }
  };

  return (
    <>
      <ProgressBar progress={0} MB={7} />
      <div className="w-full  sm:w-[70%] xl:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Income Details" />

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
          {fields.map((field, index) => {
            const isRentalIncome = incomeTypes[index]?.type === "rental_income";
            const selectedTypes = incomeTypes
              .filter((_, i) => i !== index)
              .map((item) => item.type)
              .filter((type) => type && type !== "rental_income");

            const filteredOptions = otherIncomeOptions.filter(
              (option) => !selectedTypes.includes(option.value)
            );
            return (
              <React.Fragment key={field.id}>
                {index < 4 ? (
                  <>
                    <FormGroup className="relative">
                      <IncomeInputs
                        label={
                          index === 0
                            ? "Gross Salary/Income"
                            : index === 1
                            ? "Overtime/shift allowance"
                            : index === 2
                            ? "Bonus/Commission"
                            : "Gross secondary/Business income"
                        }
                        name={`income.${index}.amount`}
                        control={control}
                        type="number"
                        required={index === 0 ? true : false}
                        placeholder="$200,000"
                        error={errors.income?.[index]?.amount?.message}
                        className="flex-1"
                        rules={{
                          validate: (value: string | number) => {
                            if (
                              value === "" ||
                              value === null ||
                              value === undefined
                            )
                              return "Amount is required";
                            if (Number(value) < 0)
                              return "Value cannot be negative";
                            return true;
                          },
                        }}
                        selectlabel="frequency"
                        selectname={`income.${index}.frequency`}
                        selectoptions={frequencyOptions}
                        selectplaceholder={"annually"}
                        selectrequired={true}
                        selecterror={errors.income?.[index]?.frequency?.message}
                      />
                    </FormGroup>
                  </>
                ) : (
                  <>
                    <FormGroup className="relative ">
                      <SelectField
                        label="Other"
                        name={`income.${index}.type`}
                        control={control}
                        // options={otherIncomeOptions}
                        options={filteredOptions}
                        placeholder="Government benefits"
                        error={
                          (errors.income?.[index]?.type as FieldError)?.message
                        }
                        required={true}
                        className="flex-1"
                        isDisabled={!!income?.[index]?.dbId}
                        removeGrayBg={true}
                      />
                      <div className="flex-1">
                        <label className="absolute block font-Mulish text-base font-normal text-lightBlue mb-1">
                          Income
                        </label>
                        <IncomeInputs
                          label={"income"}
                          hidelabel={true}
                          name={`income.${index}.amount`}
                          control={control}
                          type="number"
                          required={true}
                          placeholder="$1000"
                          error={errors.income?.[index]?.amount?.message}
                          className="flex-1 mt-7 md:mt-7 sm:mt-7 lg:mt-0"
                          rules={{
                            validate: (value: string | number) => {
                              if (
                                value === "" ||
                                value === null ||
                                value === undefined
                              )
                                return "Amount is required";
                              if (Number(value) < 0)
                                return "Value cannot be negative";
                              return true;
                            },
                          }}
                          selectlabel="frequency"
                          selectname={`income.${index}.frequency`}
                          selectoptions={frequencyOptions}
                          selecterror={
                            errors.income?.[index]?.frequency?.message
                          }
                          selectrequired={true}
                          selectplaceholder={"annually"}
                          customWidth={true}
                        />
                      </div>
                      {index > 3 && (
                        <RemoveItem
                          className="absolute right-0 "
                          disabled={deleteIsPending}
                          onClick={() => {
                            if (field?.dbId) {
                              deleteItem(
                                `/income/${savedIds?.applicantID}/incomes/${field?.dbId}`,
                                {
                                  onSuccess: () => {
                                    remove(index);
                                    const updatedIncome = income?.filter(
                                      (item) => item.dbId !== field.dbId
                                    );
                                    dispatch(
                                      addStepsData({ income: updatedIncome })
                                    );
                                  },
                                }
                              );
                            } else {
                              remove(index);
                            }
                          }}
                        />
                      )}
                    </FormGroup>

                    {isRentalIncome && (
                      <>
                        <FormGroup>
                          <SelectField
                            label="Property Type"
                            name={`income.${index}.propertyType`}
                            control={control}
                            options={propertyTypeOptions}
                            placeholder="Apartment"
                            error={
                              errors.income?.[index]?.propertyType?.message
                            }
                            required={true}
                            className="flex-1"
                          />
                          <SelectField
                            label="No. of title holders"
                            name={`income.${index}.noOfTitleHolders`}
                            control={control}
                            options={holdersOptions}
                            placeholder="2"
                            error={
                              errors.income?.[index]?.noOfTitleHolders
                                ?.message as string | undefined
                            }
                            required={true}
                            className="flex-1"
                          />
                        </FormGroup>
                        <FormGroup>
                          <InputField
                            label={`Property Expense`}
                            name={`income.${index}.propertyExpense`}
                            control={control}
                            type="number"
                            required={false}
                            placeholder="$1000"
                            error={
                              errors.income?.[index]?.propertyExpense
                                ?.message as string | undefined
                            }
                            className="flex-1"
                            // rules={{
                            //   min: {
                            //     value: 1,
                            //     message: "value must be greater than 0",
                            //   },
                            // }}
                            rules={{
                              validate: (value: any) => {
                                const amount = watch(`income.${index}.amount`);
                                if (!value) return true;
                                if (Number(value) > Number(amount)) {
                                  return "Property expense cannot be greater than income amount";
                                }
                                return true;
                              },
                            }}
                          />
                          <AutocompleteLocations
                            FieldName={`income.${index}.propertyAddress`}
                            placeholder="2464 Royal Ln. Mesa..."
                            register={register}
                            setValue={setValue}
                            setError={setError}
                            label="Property address"
                            required={true}
                            className="flex-1"
                            error={
                              errors.income?.[index]?.propertyAddress
                                ?.message as string | undefined
                            }
                            clearErrors={clearErrors}
                          />
                        </FormGroup>
                      </>
                    )}
                  </>
                )}
              </React.Fragment>
            );
          })}
          {fields.length < 11 && (
            <div className="flex items-center justify-end mt-6">
              <AddMore
                onClick={() => append({ type: "", amount: "", frequency: "" })}
              />
            </div>
          )}

          <div className="flex justify-between my-10">
            <PreviousButtom onClick={handlePrevious}>Previous</PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default IncomeDetailsPartOne;
